import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import {
  ChevronDown,
  ChevronUp,
  Clipboard,
  ExternalLink,
  Loader2,
  Square,
} from "lucide-react"

import { Button } from "@f2e/ui/components/base"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@f2e/ui/components/base"
import { Input } from "@f2e/ui/components/base"
import { Label } from "@f2e/ui/components/base"

import { DEFAULT_WEBSITE_CRAWL_CREDIT_RATE_PER_PAGE } from "@/constants/billing"

import type {
  WebsiteCrawlBillingSnapshot,
  WebsiteCrawlFormState,
  UseWebsiteCrawlerDialogReturn,
} from "./hooks/useWebsiteCrawlerDialog"

type WebsiteCrawlerDialogMode = "create" | "manage"

const ceilCredits = (value: number) =>
  Math.ceil(Math.max(0, value) - Number.EPSILON)

export interface WebsiteCrawlerDialogProps {
  mode: WebsiteCrawlerDialogMode
  folderName?: string
  open: boolean
  onOpenChange: (isOpen: boolean) => void
  form: WebsiteCrawlFormState
  onFormChange: (patch: Partial<WebsiteCrawlFormState>) => void
  target?: UseWebsiteCrawlerDialogReturn["target"]
  job?: UseWebsiteCrawlerDialogReturn["job"]
  isJobActive?: boolean
  isDirty?: boolean
  canSave?: boolean
  canSync?: boolean
  isSaving?: boolean
  isSyncing?: boolean
  isCanceling?: boolean
  failureDetails?: UseWebsiteCrawlerDialogReturn["failureDetails"]
  failureDetailsTotal?: number
  failureDetailsStatus?: UseWebsiteCrawlerDialogReturn["failureDetailsStatus"]
  isFailureDetailsOpen?: boolean
  isLoadingFailureDetails?: boolean
  websiteCrawlCreditRatePerPage?: number
  availableCredits?: number | null
  onLoadBillingOverview?: () => Promise<WebsiteCrawlBillingSnapshot>
  onShowFailureDetails?: UseWebsiteCrawlerDialogReturn["showFailureDetails"]
  onFailureDetailsOpenChange?: UseWebsiteCrawlerDialogReturn["setFailureDetailsOpen"]
  isSubmitting?: boolean
  canSubmit?: boolean
  onSave?: () => void
  onSync: () => void
  onCancel?: () => void
}

export const WebsiteCrawlerDialog = ({
  mode,
  folderName = "",
  open,
  onOpenChange,
  form,
  onFormChange,
  target,
  job,
  isJobActive = false,
  isDirty = false,
  canSave = false,
  canSync = false,
  isSaving = false,
  isSyncing = false,
  isCanceling = false,
  failureDetails = [],
  failureDetailsTotal = 0,
  failureDetailsStatus = "DOWNLOAD_FAILED",
  isFailureDetailsOpen = false,
  isLoadingFailureDetails = false,
  websiteCrawlCreditRatePerPage = DEFAULT_WEBSITE_CRAWL_CREDIT_RATE_PER_PAGE,
  availableCredits = null,
  onLoadBillingOverview,
  onShowFailureDetails,
  onFailureDetailsOpenChange,
  isSubmitting = false,
  canSubmit = false,
  onSave,
  onSync,
  onCancel,
}: WebsiteCrawlerDialogProps) => {
  const { t, i18n } = useTranslation()
  const [maxDepthInput, setMaxDepthInput] = useState(String(form.maxDepth))
  const [limitInput, setLimitInput] = useState(String(form.limit))
  const [expandedFailureId, setExpandedFailureId] = useState<number | null>(
    null
  )
  const [isSyncConfirmOpen, setIsSyncConfirmOpen] = useState(false)
  const [isPreparingSync, setIsPreparingSync] = useState(false)
  const [billingSnapshot, setBillingSnapshot] =
    useState<WebsiteCrawlBillingSnapshot | null>(null)
  const isCreateMode = mode === "create"
  const hasDownloadFailures = (job?.download_failed_urls || 0) > 0
  const hasProcessFailures = (job?.indexing_failed_urls || 0) > 0
  const hasFailures = hasDownloadFailures || hasProcessFailures
  const activeCreditRate =
    billingSnapshot?.websiteCrawlCreditRatePerPage ??
    websiteCrawlCreditRatePerPage
  const activeAvailableCredits = billingSnapshot
    ? billingSnapshot.availableCredits
    : availableCredits
  const estimatedReservedCredits = ceilCredits(form.limit * activeCreditRate)
  const hasBillingEstimate = estimatedReservedCredits > 0
  const isEstimatedOverBalance =
    activeAvailableCredits !== null &&
    estimatedReservedCredits > activeAvailableCredits

  const formatCredits = (value: number) =>
    new Intl.NumberFormat(undefined, {
      maximumFractionDigits: 2,
    }).format(Math.max(0, value))

  const numberFromJob = (value: string | number | undefined | null) => {
    const parsed = Number(value ?? 0)
    return Number.isFinite(parsed) ? parsed : 0
  }

  const renderJobStatus = (status: string) => {
    const key = `knowledgeBase.treeView.websiteCrawlJobStatus.${status.toLowerCase()}`
    const translated = t(key)
    return translated === key ? status : translated
  }

  useEffect(() => {
    setMaxDepthInput(String(form.maxDepth))
  }, [form.maxDepth])
  useEffect(() => {
    setLimitInput(String(form.limit))
  }, [form.limit])

  const commitMaxDepth = () => {
    const parsed = Number.parseInt(maxDepthInput, 10)
    const next = Number.isFinite(parsed) ? Math.max(1, parsed) : form.maxDepth
    onFormChange({ maxDepth: next })
    setMaxDepthInput(String(next))
  }

  const commitLimit = () => {
    const parsed = Number.parseInt(limitInput, 10)
    const next = Number.isFinite(parsed) ? Math.max(10, parsed) : form.limit
    onFormChange({ limit: next })
    setLimitInput(String(next))
  }

  const copyText = (value: string) => {
    void navigator.clipboard?.writeText(value)
  }

  const showFailureDetails = (
    status: UseWebsiteCrawlerDialogReturn["failureDetailsStatus"]
  ) => {
    setExpandedFailureId(null)
    void onShowFailureDetails?.(status)
  }

  const toggleFailureDetails = () => {
    if (isFailureDetailsOpen) {
      onFailureDetailsOpenChange?.(false)
      return
    }
    showFailureDetails(hasDownloadFailures ? "DOWNLOAD_FAILED" : "FAILED")
  }

  const openSyncConfirmation = async () => {
    if (!canSync || isPreparingSync) return
    setIsPreparingSync(true)
    try {
      const snapshot = await onLoadBillingOverview?.()
      setBillingSnapshot(
        snapshot ?? {
          websiteCrawlCreditRatePerPage,
          availableCredits,
        }
      )
      setIsSyncConfirmOpen(true)
    } finally {
      setIsPreparingSync(false)
    }
  }

  const confirmSync = () => {
    setIsSyncConfirmOpen(false)
    void onSync()
  }

  const renderFailureStatusLabel = (
    status: UseWebsiteCrawlerDialogReturn["failureDetailsStatus"]
  ) =>
    status === "DOWNLOAD_FAILED"
      ? t("knowledgeBase.treeView.websiteCrawlFailureDownload")
      : t("knowledgeBase.treeView.websiteCrawlFailureProcess")

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-[680px]">
        <DialogHeader>
          <DialogTitle>
            {isCreateMode
              ? t("knowledgeBase.treeView.newWebsiteCrawl")
              : t("knowledgeBase.treeView.websiteCrawlTitle")}
          </DialogTitle>
          <DialogDescription>
            {isCreateMode
              ? t("knowledgeBase.treeView.newWebsiteCrawlDescription")
              : t("knowledgeBase.treeView.websiteCrawlDescription", {
                  folderName,
                })}
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-2">
          <div className="grid gap-2">
            <Label htmlFor="website-crawl-start-url">
              {t("knowledgeBase.treeView.websiteCrawlStartUrl")}
            </Label>
            <Input
              id="website-crawl-start-url"
              value={form.startUrl}
              onChange={(e) => onFormChange({ startUrl: e.target.value })}
              placeholder="https://docs.example.com"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="grid gap-2">
              <Label htmlFor="website-crawl-max-depth">
                {t("knowledgeBase.treeView.websiteCrawlMaxDepth")}
              </Label>
              <Input
                id="website-crawl-max-depth"
                type="text"
                inputMode="numeric"
                min={1}
                max={10}
                value={maxDepthInput}
                onChange={(e) => {
                  const value = e.target.value
                  if (/^\d*$/.test(value)) setMaxDepthInput(value)
                }}
                onBlur={commitMaxDepth}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="website-crawl-limit">
                {t("knowledgeBase.treeView.websiteCrawlLimit")}
              </Label>
              <Input
                id="website-crawl-limit"
                type="text"
                inputMode="numeric"
                min={10}
                max={5000}
                value={limitInput}
                onChange={(e) => {
                  const value = e.target.value
                  if (/^\d*$/.test(value)) setLimitInput(value)
                }}
                onBlur={commitLimit}
              />
            </div>
          </div>
          <p className="-mt-1 text-xs text-slate-500 dark:text-slate-400">
            {t("knowledgeBase.treeView.websiteCrawlLimitHelp")}
          </p>
          <div className="mt-2 grid gap-1">
            <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
              {t("knowledgeBase.treeView.websiteCrawlPathPatterns")}
            </h3>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              {t("knowledgeBase.treeView.websiteCrawlPathPatternsHelp")}
            </p>
          </div>
          <div className="grid gap-2">
            <Label htmlFor="website-crawl-include-paths">
              {t("knowledgeBase.treeView.websiteCrawlIncludePaths")}
            </Label>
            <Input
              id="website-crawl-include-paths"
              value={form.includePaths}
              onChange={(e) => onFormChange({ includePaths: e.target.value })}
              placeholder="/docs,/guides/*"
            />
            <p className="text-xs text-slate-500 dark:text-slate-400">
              {t("knowledgeBase.treeView.websiteCrawlIncludePathsHelp")}
            </p>
          </div>
          <div className="grid gap-2">
            <Label htmlFor="website-crawl-exclude-paths">
              {t("knowledgeBase.treeView.websiteCrawlExcludePaths")}
            </Label>
            <Input
              id="website-crawl-exclude-paths"
              value={form.excludePaths}
              onChange={(e) => onFormChange({ excludePaths: e.target.value })}
              placeholder="/login,/zh-TW/products/禮盒一批*"
            />
          </div>
          <div className="grid gap-2">
            <label className="inline-flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300">
              <input
                type="checkbox"
                checked={form.allowSubdomains}
                onChange={(e) =>
                  onFormChange({ allowSubdomains: e.target.checked })
                }
              />
              {t("knowledgeBase.treeView.websiteCrawlAllowSubdomains")}
            </label>
            <label className="inline-flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300">
              <input
                type="checkbox"
                checked={form.ignoreQueryParameters}
                onChange={(e) =>
                  onFormChange({ ignoreQueryParameters: e.target.checked })
                }
              />
              {t("knowledgeBase.treeView.websiteCrawlIgnoreQueryParameters")}
            </label>
            <label className="inline-flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300">
              <input
                type="checkbox"
                checked={form.ignoreSitemap}
                onChange={(e) =>
                  onFormChange({ ignoreSitemap: e.target.checked })
                }
              />
              {t("knowledgeBase.treeView.websiteCrawlIgnoreSitemap")}
            </label>
            <label className="inline-flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300">
              <input
                type="checkbox"
                checked={form.onlyMainContent}
                onChange={(e) =>
                  onFormChange({ onlyMainContent: e.target.checked })
                }
              />
              {t("knowledgeBase.treeView.websiteCrawlOnlyMainContent")}
            </label>
          </div>
          {!isCreateMode && target && (
            <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1 text-xs text-muted-foreground">
              <span>
                {t("knowledgeBase.treeView.websiteCrawlLastSyncedAt")}
              </span>
              {target.last_synced_at ? (
                <time dateTime={target.last_synced_at}>
                  {new Date(target.last_synced_at).toLocaleString(
                    i18n.language
                  )}
                </time>
              ) : (
                <span>
                  {t("knowledgeBase.treeView.websiteCrawlNoSyncRecord")}
                </span>
              )}
            </div>
          )}
          {!isCreateMode && target?.kb_root_missing && (
            <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:border-amber-700 dark:bg-amber-900/40 dark:text-amber-200">
              {t("knowledgeBase.treeView.websiteCrawlFolderMissing")}
            </div>
          )}
          {!isCreateMode && job && (
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 dark:border-slate-700 dark:bg-slate-800/50">
              <div className="mb-2 flex items-center justify-between text-xs text-slate-600 dark:text-slate-300">
                <span>
                  {isJobActive
                    ? t("knowledgeBase.treeView.websiteCrawlSyncing")
                    : t("knowledgeBase.treeView.websiteCrawlStatus")}
                </span>
                <div className="flex items-center gap-2">
                  <span>
                    {isJobActive
                      ? t("knowledgeBase.treeView.websiteCrawlStatusRunning")
                      : renderJobStatus(job.status)}
                  </span>
                  {isJobActive && onCancel && (
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={onCancel}
                      disabled={isCanceling || isSaving || isSyncing}
                      aria-label={t("knowledgeBase.treeView.cancel")}
                      title={t("knowledgeBase.treeView.cancel")}
                      className="h-6 w-6"
                    >
                      <Square className="w-3 h-3" />
                    </Button>
                  )}
                </div>
              </div>
              <div className="space-y-1 text-xs text-slate-600 dark:text-slate-300">
                <p className="font-medium">
                  {t("knowledgeBase.treeView.websiteCrawlCrawlerStats")}
                </p>
                <p className="flex flex-wrap items-center gap-x-1 gap-y-1">
                  {t("knowledgeBase.treeView.websiteCrawlCrawlerCounters", {
                    discovered: job.discovered_urls,
                    downloaded: job.downloaded_urls,
                    downloadFailed: job.download_failed_urls,
                  })}
                </p>
              </div>
              <div className="mt-2 space-y-1 text-xs text-slate-600 dark:text-slate-300">
                <p className="font-medium">
                  {t("knowledgeBase.treeView.websiteCrawlIndexingStats")}
                </p>
                <p>
                  {t("knowledgeBase.treeView.websiteCrawlIndexingCounters", {
                    indexed: job.indexed_urls,
                    failed: job.indexing_failed_urls,
                    skipped: job.skipped_urls,
                  })}
                </p>
              </div>
              {job.billing_status && job.billing_status !== "not_started" && (
                <div className="mt-2 space-y-1 text-xs text-slate-600 dark:text-slate-300">
                  <p className="font-medium">
                    {t("knowledgeBase.treeView.websiteCrawlBillingStats")}
                  </p>
                  {job.billing_status === "finalized" ? (
                    <p>
                      {t(
                        "knowledgeBase.treeView.websiteCrawlBillingFinalized",
                        {
                          charged: formatCredits(
                            numberFromJob(job.billing_final_credits)
                          ),
                          pages: job.billing_actual_pages || 0,
                          reserved: formatCredits(
                            numberFromJob(job.billing_reserved_credits)
                          ),
                          refunded: formatCredits(
                            Math.max(
                              numberFromJob(job.billing_reserved_credits) -
                                numberFromJob(job.billing_final_credits),
                              0
                            )
                          ),
                        }
                      )}
                    </p>
                  ) : job.billing_status === "released" ? (
                    <p>
                      {t("knowledgeBase.treeView.websiteCrawlBillingReleased", {
                        reserved: formatCredits(
                          numberFromJob(job.billing_reserved_credits)
                        ),
                      })}
                    </p>
                  ) : (
                    <p>
                      {t("knowledgeBase.treeView.websiteCrawlBillingReserved", {
                        reserved: formatCredits(
                          numberFromJob(job.billing_reserved_credits)
                        ),
                        rate: formatCredits(
                          numberFromJob(job.billing_credit_rate_per_page)
                        ),
                      })}
                    </p>
                  )}
                </div>
              )}
              {hasFailures && (
                <div className="mt-3 border-t border-slate-200 pt-3 dark:border-slate-700">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="flex flex-wrap gap-2">
                      {hasDownloadFailures && (
                        <button
                          type="button"
                          onClick={() => showFailureDetails("DOWNLOAD_FAILED")}
                          className={`rounded-md border px-2 py-1 text-xs font-medium ${
                            failureDetailsStatus === "DOWNLOAD_FAILED" &&
                            isFailureDetailsOpen
                              ? "border-red-300 bg-red-50 text-red-700 dark:border-red-800 dark:bg-red-950/40 dark:text-red-200"
                              : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300 dark:hover:bg-slate-800"
                          }`}
                        >
                          {t(
                            "knowledgeBase.treeView.websiteCrawlFailureDownloadCount",
                            { count: job.download_failed_urls }
                          )}
                        </button>
                      )}
                      {hasProcessFailures && (
                        <button
                          type="button"
                          onClick={() => showFailureDetails("FAILED")}
                          className={`rounded-md border px-2 py-1 text-xs font-medium ${
                            failureDetailsStatus === "FAILED" &&
                            isFailureDetailsOpen
                              ? "border-red-300 bg-red-50 text-red-700 dark:border-red-800 dark:bg-red-950/40 dark:text-red-200"
                              : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300 dark:hover:bg-slate-800"
                          }`}
                        >
                          {t(
                            "knowledgeBase.treeView.websiteCrawlFailureProcessCount",
                            { count: job.indexing_failed_urls }
                          )}
                        </button>
                      )}
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={toggleFailureDetails}
                      className="h-7 gap-1 px-2 text-xs"
                    >
                      {isFailureDetailsOpen ? (
                        <ChevronUp className="h-3 w-3" />
                      ) : (
                        <ChevronDown className="h-3 w-3" />
                      )}
                      {t("knowledgeBase.treeView.websiteCrawlFailureDetails")}
                    </Button>
                  </div>
                  {isFailureDetailsOpen && (
                    <div className="mt-3 rounded-md border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-900">
                      <div className="flex items-center justify-between border-b border-slate-200 px-3 py-2 text-xs text-slate-600 dark:border-slate-700 dark:text-slate-300">
                        <span className="font-medium">
                          {renderFailureStatusLabel(failureDetailsStatus)}
                        </span>
                        <span>
                          {t(
                            "knowledgeBase.treeView.websiteCrawlFailureTotal",
                            { count: failureDetailsTotal }
                          )}
                        </span>
                      </div>
                      {isLoadingFailureDetails ? (
                        <div className="flex items-center gap-2 px-3 py-4 text-xs text-slate-500 dark:text-slate-400">
                          <Loader2 className="h-3 w-3 animate-spin" />
                          {t(
                            "knowledgeBase.treeView.websiteCrawlFailuresLoading"
                          )}
                        </div>
                      ) : failureDetails.length === 0 ? (
                        <div className="px-3 py-4 text-xs text-slate-500 dark:text-slate-400">
                          {t(
                            "knowledgeBase.treeView.websiteCrawlFailuresEmpty"
                          )}
                        </div>
                      ) : (
                        <div className="max-h-72 divide-y divide-slate-200 overflow-y-auto dark:divide-slate-700">
                          {failureDetails.map((item) => {
                            const isExpanded = expandedFailureId === item.id
                            return (
                              <div key={item.id} className="px-3 py-2">
                                <div className="flex items-start gap-2">
                                  <button
                                    type="button"
                                    onClick={() =>
                                      setExpandedFailureId(
                                        isExpanded ? null : item.id
                                      )
                                    }
                                    className="min-w-0 flex-1 text-left"
                                  >
                                    <span
                                      className="block truncate text-xs font-medium text-slate-700 dark:text-slate-200"
                                      title={item.url}
                                    >
                                      {item.url}
                                    </span>
                                    <span className="mt-1 block line-clamp-2 text-xs text-red-600 dark:text-red-300">
                                      {item.error_message ||
                                        t(
                                          "knowledgeBase.treeView.websiteCrawlFailureNoError"
                                        )}
                                    </span>
                                  </button>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => copyText(item.url)}
                                    className="h-7 w-7 shrink-0"
                                    title={t(
                                      "knowledgeBase.treeView.websiteCrawlFailureCopyUrl"
                                    )}
                                    aria-label={t(
                                      "knowledgeBase.treeView.websiteCrawlFailureCopyUrl"
                                    )}
                                  >
                                    <Clipboard className="h-3 w-3" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() =>
                                      window.open(item.url, "_blank")
                                    }
                                    className="h-7 w-7 shrink-0"
                                    title={t(
                                      "knowledgeBase.treeView.websiteCrawlFailureOpenUrl"
                                    )}
                                    aria-label={t(
                                      "knowledgeBase.treeView.websiteCrawlFailureOpenUrl"
                                    )}
                                  >
                                    <ExternalLink className="h-3 w-3" />
                                  </Button>
                                </div>
                                {isExpanded && (
                                  <div className="mt-2 space-y-2 rounded-md bg-slate-50 p-2 text-xs text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                                    <div className="break-all">
                                      <span className="font-medium">
                                        {t(
                                          "knowledgeBase.treeView.websiteCrawlFailureUrl"
                                        )}
                                        :{" "}
                                      </span>
                                      {item.url}
                                    </div>
                                    <div>
                                      <span className="font-medium">
                                        {t(
                                          "knowledgeBase.treeView.websiteCrawlFailureError"
                                        )}
                                        :{" "}
                                      </span>
                                      {item.error_message ||
                                        t(
                                          "knowledgeBase.treeView.websiteCrawlFailureNoError"
                                        )}
                                    </div>
                                    {item.content_preview && (
                                      <pre className="max-h-32 overflow-auto whitespace-pre-wrap break-words rounded border border-slate-200 bg-white p-2 text-[11px] dark:border-slate-700 dark:bg-slate-950">
                                        {item.content_preview}
                                      </pre>
                                    )}
                                  </div>
                                )}
                              </div>
                            )
                          })}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t("knowledgeBase.treeView.cancel")}
          </Button>
          {isCreateMode ? (
            <>
              <Button
                variant="outline"
                onClick={onSave}
                disabled={!canSubmit || isSubmitting}
              >
                {t("knowledgeBase.treeView.save")}
              </Button>
              <Button onClick={onSync} disabled={!canSync || isSyncing}>
                {t("knowledgeBase.treeView.sync")}
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={onSave} disabled={!canSave}>
                {t("knowledgeBase.treeView.save")}
              </Button>
              <Button
                onClick={() => {
                  void openSyncConfirmation()
                }}
                disabled={!canSync || isPreparingSync}
                title={isDirty ? t("knowledgeBase.treeView.save") : undefined}
              >
                {isPreparingSync ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  t("knowledgeBase.treeView.sync")
                )}
              </Button>
            </>
          )}
        </DialogFooter>
        <Dialog open={isSyncConfirmOpen} onOpenChange={setIsSyncConfirmOpen}>
          <DialogContent className="sm:max-w-[480px]">
            <DialogHeader>
              <DialogTitle>
                {t("knowledgeBase.treeView.websiteCrawlBillingConfirmTitle")}
              </DialogTitle>
              <DialogDescription>
                {t(
                  "knowledgeBase.treeView.websiteCrawlBillingConfirmDescription"
                )}
              </DialogDescription>
            </DialogHeader>
            {hasBillingEstimate && (
              <div
                className={`rounded-md border px-3 py-2 text-sm ${
                  isEstimatedOverBalance
                    ? "border-amber-300 bg-amber-50 text-amber-800 dark:border-amber-700 dark:bg-amber-900/40 dark:text-amber-100"
                    : "border-slate-200 bg-slate-50 text-slate-600 dark:border-slate-700 dark:bg-slate-800/50 dark:text-slate-300"
                }`}
              >
                <p>
                  {t("knowledgeBase.treeView.websiteCrawlBillingEstimate", {
                    reserved: formatCredits(estimatedReservedCredits),
                    rate: formatCredits(activeCreditRate),
                  })}
                </p>
                {activeAvailableCredits !== null && (
                  <p className="mt-1">
                    {t("knowledgeBase.treeView.websiteCrawlBillingBalance", {
                      balance: formatCredits(activeAvailableCredits),
                    })}
                  </p>
                )}
                {isEstimatedOverBalance && (
                  <p className="mt-2 font-medium">
                    {t(
                      "knowledgeBase.treeView.websiteCrawlBillingInsufficientHint"
                    )}
                  </p>
                )}
              </div>
            )}
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setIsSyncConfirmOpen(false)}
              >
                {t("knowledgeBase.treeView.cancel")}
              </Button>
              <Button
                onClick={confirmSync}
                disabled={isSyncing || isEstimatedOverBalance}
              >
                {t("knowledgeBase.treeView.websiteCrawlBillingConfirmStart")}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </DialogContent>
    </Dialog>
  )
}
